// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useState, useEffect } from 'react';
import { useVaccineDashboardStore } from '../../../store/vaccineDashboardStore';
import { useVaccineClientStore } from '../../../store/vaccineClientStore';
import { ModelInputsCard } from './ModelInputsCard';
import ValidationModal from '../../DecisionTree/ValidationModal';

/**
 * Connected wrapper component for ModelInputsCard
 * Connects to Zustand store and passes clean props to presentational component
 */
export const ModelInputsCardConnected: React.FC = () => {
  const inputs = useVaccineDashboardStore((state) => state.inputs);
  const globalInputs = useVaccineDashboardStore((state) => state.globalInputs);
  const isCollapsed = useVaccineDashboardStore((state) => state.isModelInputsCollapsed);
  const patchInputs = useVaccineDashboardStore((state) => state.patchInputs);
  const resetToDefaults = useVaccineDashboardStore((state) => state.resetToDefaults);

  const handleRegionsChange = useVaccineDashboardStore((state) => state.handleRegionsChange);
  const handleToggle = useVaccineDashboardStore((state) => state.setIsModelInputsCollapsed);
  const setPopulationMode = useVaccineDashboardStore((state) => state.setPopulationMode);
  const setNationalPopulation = useVaccineDashboardStore((state) => state.setNationalPopulation);
  const setNationalCoveragePct = useVaccineDashboardStore((state) => state.setNationalCoveragePct);

  // Get validation issues from client store
  const issues = useVaccineClientStore((state) => state.issues);
  const [showModal, setShowModal] = useState(false);
  const [prevIssueCount, setPrevIssueCount] = useState(0);

  // Show modal when new errors appear
  useEffect(() => {
    const errorCount = issues.filter(i => i.severity === 'error').length;
    if (errorCount > prevIssueCount) {
      setShowModal(true);
    }
    setPrevIssueCount(errorCount);
  }, [issues, prevIssueCount]);

  // Map issues to field errors
  const errors: Record<string, string> = {};
  issues.forEach(issue => {
    if (issue.severity === 'error') {
      switch (issue.id) {
        case 'invalid-baseline-mortality':
          errors['mortalityRate'] = issue.message;
          break;
        case 'invalid-hospitalization-rate':
          errors['hospitalizationRate'] = issue.message;
          break;
        case 'invalid-mortality-hospitalization-sum':
          errors['mortalityRate'] = issue.message;
          errors['hospitalizationRate'] = issue.message;
          break;
        case 'invalid-duration-infection':
          errors['durationInfection'] = issue.message;
          break;
        case 'invalid-utility-infection':
          errors['utilityInfection'] = issue.message;
          break;
        case 'invalid-cycles':
          errors['timeHorizon'] = issue.message;
          break;
        case 'invalid-discount-rate':
          errors['discountRate'] = issue.message;
          break;
        // Add other mappings as needed
      }
    }
  });

  return (
    <>
      <ModelInputsCard
        inputs={inputs}
        isCollapsed={isCollapsed}
         populationMode={globalInputs.populationMode}
         nationalPopulation={inputs.population}
         nationalCoverage={globalInputs.nationalCoveragePct}
         errors={errors}
        actions={{
          toggle: () => handleToggle(!isCollapsed),
          reset: resetToDefaults,
          changeRegions: handleRegionsChange,
          updateInput: patchInputs,
          changePopulationMode: setPopulationMode,
          changeNationalPopulation: setNationalPopulation,
          changeNationalCoverage: setNationalCoveragePct,
        }}
      />
      <ValidationModal
        isOpen={showModal}
        issues={issues.filter(i => i.severity === 'error')}
        onClose={() => setShowModal(false)}
      />
    </>
  );
};

